import { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Heading, Paragraph, Tag } from '@digdir/designsystemet-react';
import type { TagColor } from '../inspectionReportView';
import classes from '../InspectionReport.module.css';

type SectionProps = {
  id: string;
  /** «Del 1», or «Før du leser» when there is no number */
  part: number | null;
  title: string;
  intro?: string;
  /** Starts on a new page when printed */
  newPage?: boolean;
  children: ReactNode;
};

export const ReportSection = ({ id, part, title, intro, newPage, children }: SectionProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <section aria-labelledby={id} className={`${classes.section} ${newPage ? classes.breakBefore : ''}`}>
      <div className={classes.sectionHeading}>
        <Paragraph data-size="sm" className={classes.part}>
          {part === null
            ? t('workEnvironmentInspection.parts.before')
            : t('workEnvironmentInspection.parts.part', { number: part })}
        </Paragraph>
        <Heading level={2} data-size="md" id={id}>
          {title}
        </Heading>
      </div>
      {intro && <Paragraph className={classes.lead}>{intro}</Paragraph>}
      {children}
    </section>
  );
};

type Figure = { key: string; label: string; value: string; sub: string };

/** Key numbers, each in a box */
export const Figures = ({ items }: { items: Figure[] }): ReactElement => (
  <dl className={classes.figures}>
    {items.map((item) => (
      <div key={item.key} className={classes.figure}>
        <dt className={`ds-paragraph ${classes.subtle}`} data-size="xs">
          {item.label}
        </dt>
        <dd className="ds-heading" data-size="sm">
          {item.value}
        </dd>
        {item.sub && (
          <dd className={`ds-paragraph ${classes.subtle}`} data-size="xs">
            {item.sub}
          </dd>
        )}
      </div>
    ))}
  </dl>
);

export const StatusTag = ({ label, color }: { label: string; color: TagColor }): ReactElement => (
  <Tag data-size="sm" data-color={color} className={classes.startTag}>
    {label}
  </Tag>
);
